import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Users, Calendar, Broadcast, StopCircle } from "@phosphor-icons/react";
import StatusBadge from "@/components/StatusBadge";
import ViewerBanner from "@/components/ViewerBanner";
import GoLiveDialog from "@/components/GoLiveDialog";
import { useIsViewer } from "@/lib/permissions";
import { toast } from "sonner";

export default function CompanyJobs() {
  const viewer = useIsViewer();
  const [items, setItems] = useState([]);
  const [liveJob, setLiveJob] = useState(null);
  const load = () => api.get("/company/jobs").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.patch(`/company/jobs/${id}?status=${status}`); toast.success("Updated"); load(); } catch { toast.error("Failed"); }
  };
  const stopLive = async (id) => {
    try { await api.post(`/company/jobs/${id}/stop-live`); toast("Live dispatch stopped"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4">
      {viewer && <ViewerBanner />}
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">My jobs</h1>
        {!viewer && <Link to="/company/jobs/new"><Button className="bg-blue-600 hover:bg-blue-700 h-11"><Plus size={16} className="mr-1.5"/> New job</Button></Link>}
      </div>
      {items.length === 0 && <div className="text-slate-500 py-10 text-center">No jobs yet.</div>}
      <div className="space-y-3">
        {items.map((j) => (
          <div key={j.id} data-testid={`co-job-${j.id}`} className="card-tactile p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="label-caps text-blue-600 mb-0.5">{j.category}</div>
                <div className="font-display text-xl font-bold text-slate-900">{j.title}</div>
                <div className="mt-1 flex flex-wrap gap-3 text-sm text-slate-700">
                  <span className="flex items-center gap-1"><Calendar size={14}/> {j.date}</span>
                  <span>{j.start_time}–{j.end_time}</span>
                  <span className="flex items-center gap-1"><Users size={14}/> {j.staff_booked}/{j.staff_required} booked</span>
                  <span>£{j.pay_rate}/hr</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={j.status} />
                {j.live_dispatch && j.dispatch_status === "dispatching" && (
                  <span data-testid={`live-badge-${j.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 animate-pulse">
                    <Broadcast size={12} weight="fill"/> LIVE
                  </span>
                )}
                {!viewer && j.status === "published" && !j.live_dispatch && (
                  <Button data-testid={`go-live-${j.id}`} size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setLiveJob(j)}>
                    <Broadcast size={14} weight="fill" className="mr-1"/> Go live
                  </Button>
                )}
                {!viewer && j.live_dispatch && j.dispatch_status !== "filled" && (
                  <Button data-testid={`stop-live-${j.id}`} size="sm" variant="outline" className="border-red-300 text-red-700" onClick={() => stopLive(j.id)}>
                    <StopCircle size={14} className="mr-1"/> Stop live
                  </Button>
                )}
                {!viewer && j.status === "published" && <Button variant="outline" size="sm" onClick={()=>setStatus(j.id, "paused")}>Pause</Button>}
                {!viewer && j.status === "paused" && <Button variant="outline" size="sm" onClick={()=>setStatus(j.id, "published")}>Resume</Button>}
                {!viewer && j.status === "draft" && <Button variant="outline" size="sm" onClick={()=>setStatus(j.id, "published")}>Publish</Button>}
              </div>
            </div>
          </div>
        ))}
      </div>
      <GoLiveDialog open={!!liveJob} onOpenChange={(v) => !v && setLiveJob(null)} job={liveJob} onDone={load} />
    </div>
  );
}
